// =============================================================================
// Order Summary Card — read-only fulfillment view (portal)
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Card, Badge, DesignPartsSection } from '@tkpod/ui';
import type { DesignPartInfo } from '@tkpod/ui';
import {
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  orderItemDisplayName,
  orderItemStoreListingTitle,
  formatOrderedOptionValue,
  orderedVariantLabel,
} from '@tkpod/shared';
import { OptionUploadVersionActions } from './OptionUploadVersionActions';

interface OrderItem {
  id: string;
  title: string;
  /** Catalog product a PRODUCT line is built from. Null for custom products. */
  catalogTitle?: string | null;
  /** Ordered variant options label, e.g. "White / S" (apparel/POD lines). */
  variantTitle?: string | null;
  /** Raw option snapshot — shape varies by ingest lane (Shopify: record map,
   *  manual: `[{ name, value }]` array). Fallback label source when
   *  variantTitle was not captured at ingest. */
  productOptions?: unknown | null;
  customization?: {
    submissionToken?: string | null;
    pricing?: {
      variantUnitPriceCents: number | null;
      surchargePerUnitCents: number | null;
      lines: { label: string; amountCentsPerUnit: number }[];
      formulaWidth?: number | null;
      formulaHeight?: number | null;
      formulaUnitLabel?: string | null;
      formulaRateCents?: number | null;
      displayFormulaUnitCents?: number | null;
      displayDiscountPercent?: number | null;
      displayCustomerUnitCents?: number | null;
      displayLineTotalCents?: number | null;
    } | null;
    selections: { label: string; value: string }[];
    uploads: {
      token?: string | null;
      label?: string | null;
      fileName: string | null;
      mimeType?: string | null;
      sizeBytes?: number | null;
      widthPx?: number | null;
      heightPx?: number | null;
      status?: string | null;
      scanStatus?: string | null;
      versionNo: number | null;
      thumbUrl: string | null;
      previewUrl: string | null;
      downloadUrl: string | null;
    }[];
  } | null;
  /** Order-time blank variant snapshot (manual lane) — carries
   *  `options: [{ type, value }]`, the last-resort label source. */
  variantSnapshot?: unknown | null;
  sku: string | null;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  uptName: string | null;
  presetName: string | null;
  providerName: string | null;
  weightOz?: number | null;
  routingSource?: string | null;
  externalSku?: string | null;
  designId?: string | null;
  designRefId?: string | null;
  partKey?: string | null;
  /** Stable customer-facing preview PNG URL (self-heals from placeholder). */
  designPreviewUrlStable?: string | null;
  /** Stable full-export PNG URL. */
  designExportUrlStable?: string | null;
  designPreviewUrl?: string | null;
  /**
   * PRODUCT-context (published POD product) line preview: the ordered variant's
   * pre-rendered hero composite (mockup + design). Null for apparel / gangsheet
   * lines, which carry their own COMPOSITE preview.
   */
  variantPreviewUrl?: string | null;
  exportId?: string | null;
  exportStatus?: string | null;
  exportOutputUrl?: string | null;
  exportPreviewUrl?: string | null;
  sheetIndex?: number | null;
  actualWidthIn?: number | null;
  actualHeightIn?: number | null;
  designContext?: string | null;
  designParts?: DesignPartInfo[];
  /** Order-time fulfillment snapshot — drives the Self-fulfill line badge. */
  fulfillmentProvider?: 'TK_POD' | 'SELF';
  fulfillmentMethod?: 'AUTO' | 'MANUAL';
  /**
   * Per-line post-order edit permission (resolved server-side from the
   * fulfillment/production matrix). Drives the inline "Edit design" action.
   */
  editPermission?: {
    canEdit: boolean;
    reason?: string | null;
    message?: string | null;
  } | null;
  /**
   * Customer-facing Edit link (Shopify App Proxy URL) — the same link the
   * buyer received on their order line. Null for non-Shopify / non-STOREFRONT
   * lines. Rendered as an open + copy row so merchants can re-share it.
   */
  customerEditUrl?: string | null;
}

const ROUTING_SOURCE_LABELS: Record<string, string> = {
  VARIANT_MAPPING: 'Variant Mapping',
  PRODUCT_MAPPING: 'Product Mapping',
  FULFILLMENT_RULE: 'Fulfillment Rule',
  DEFAULT_FALLBACK: 'Default Fallback',
  MANUAL_OVERRIDE: 'Manual Override',
};

const ROUTING_SOURCE_COLORS: Record<string, string> = {
  VARIANT_MAPPING: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
  PRODUCT_MAPPING: 'bg-sky-500/10 text-sky-600 border-sky-500/20',
  FULFILLMENT_RULE: 'bg-teal-500/10 text-teal-700 border-teal-500/20',
  DEFAULT_FALLBACK: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  MANUAL_OVERRIDE: 'bg-orange-500/10 text-orange-700 border-orange-500/20',
  UNROUTED: 'bg-red-500/10 text-red-700 border-red-500/20',
};

interface OrderSummaryCardProps {
  orderNumber: string;
  status: string;
  provider: { id: string; name: string } | null;
  warehouse: { id: string; name: string; code: string } | null;
  items: OrderItem[];
  createdAt: string;
  /**
   * Launch the designer for a line's bound design (post-order revision flow).
   * When omitted, the inline "Edit design" action is hidden — keeps the card
   * read-only for surfaces that don't support post-order edits.
   */
  onEditDesign?: (itemId: string) => void;
  /** Surface a restriction message when an edit is blocked (e.g. in production). */
  onEditRestricted?: (message: string) => void;
  /** Item id currently minting a launch token — drives the per-row spinner label. */
  editPendingItemId?: string | null;
}

export function OrderSummaryCard({
  orderNumber,
  status,
  provider,
  warehouse,
  items,
  createdAt,
  onEditDesign,
  onEditRestricted,
  editPendingItemId,
}: OrderSummaryCardProps) {
  const warehouseLabel = warehouse ? `${warehouse.name} (${warehouse.code})` : null;
  const isTerminal = ['DELIVERED', 'CANCELLED', 'FAILED'].includes(status);

  // Transient "Copied" feedback for the per-line customer edit link.
  const [copiedItemId, setCopiedItemId] = useState<string | null>(null);
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
    },
    [],
  );
  const copyEditLink = async (itemId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedItemId(itemId);
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopiedItemId(null), 2000);
    } catch {
      // Clipboard unavailable (permissions/insecure context) — the link is
      // still openable/right-clickable, so fail silently.
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge
            variant={ORDER_STATUS_MERCHANT_VARIANT[status] ?? 'default'}
            size="sm"
          >
            {getMerchantOrderStatusLabel(status)}
          </Badge>
          {warehouseLabel && (
            <span className="truncate text-xs text-text-secondary">{warehouseLabel}</span>
          )}
        </div>
        <span className="shrink-0 font-mono text-[10px] text-text-tertiary sm:inline">
          {orderNumber}
        </span>
      </div>

      <div className="border-t border-border px-4 py-2 sm:px-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-text-secondary">
          <span>
            {new Date(createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          {provider ? (
            <>
              <span className="text-text-tertiary">·</span>
              <span className="font-medium">{provider.name}</span>
            </>
          ) : (
            <>
              <span className="text-text-tertiary">·</span>
              <span className="italic text-text-tertiary">No provider assigned</span>
            </>
          )}
        </div>
      </div>

      <div className="border-t border-border">
        <div className="px-4 py-2 sm:px-5">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            Items ({items.length})
          </h3>
        </div>
        <div className="divide-y divide-border">
          {items.map((item) => {
            const isSelfFulfilled = item.fulfillmentProvider === 'SELF';
            const itemProvider = item.providerName ?? provider?.name ?? null;
            const isUnrouted =
              !isSelfFulfilled &&
              (!item.routingSource || item.routingSource === 'UNROUTED') &&
              !itemProvider;
            const routingLabel = item.routingSource && item.routingSource !== 'UNROUTED'
              ? ROUTING_SOURCE_LABELS[item.routingSource] ?? item.routingSource
              : null;

            return (
              <div
                key={item.id}
                className="flex items-start gap-3 px-4 py-3 sm:px-5"
              >
                {(() => {
                  // Gangsheet lines (sheetIndex set) own a single SHEET part —
                  // use its preview as the product image so each line shows its
                  // own sheet instead of an empty placeholder. The URL is
                  // stable and serves a placeholder until the render lands.
                  const sheetPreview =
                    item.sheetIndex != null
                      ? item.designParts?.find((p) => p.kind === 'SHEET')
                          ?.previewUrl ?? null
                      : null;
                  // PRODUCT-context lines: prefer the ordered variant's hero
                  // composite (mockup + design) over the generic stable preview.
                  const uploadPreview = item.customization?.uploads.find(
                    (file) => file.thumbUrl || file.previewUrl,
                  );
                  const previewSrc = item.variantPreviewUrl
                    || item.designPreviewUrlStable
                    || item.exportPreviewUrl
                    || (item.sheetIndex == null ? item.designPreviewUrl : sheetPreview)
                    || uploadPreview?.thumbUrl
                    || uploadPreview?.previewUrl;
                  return previewSrc ? (
                    <img
                      src={previewSrc}
                      alt="Design preview"
                      className="mt-0.5 h-8 w-8 shrink-0 rounded-md border border-border object-cover bg-surface-secondary"
                    />
                  ) : (
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface-secondary text-xs">
                      {item.exportStatus === 'COMPLETED' ? '🖨️' : (
                        <svg className="h-4 w-4 animate-spin text-text-tertiary" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                    </div>
                  );
                })()}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="truncate text-sm font-medium text-text-primary">
                          {orderItemDisplayName(item)}
                        </p>
                        {isSelfFulfilled && (
                          <Badge variant="warning" size="sm">
                            Self-fulfill
                          </Badge>
                        )}
                        {!isSelfFulfilled && item.fulfillmentMethod === 'MANUAL' && (
                          <Badge variant="info" size="sm">
                            Manual
                          </Badge>
                        )}
                      </div>
                      {orderItemStoreListingTitle(item) && (
                        <p className="truncate text-xs text-text-tertiary">
                          Store listing: {orderItemStoreListingTitle(item)}
                        </p>
                      )}
                      {(() => {
                        // Ordered variant options ("White / S") — apparel and
                        // POD lines carry variantTitle from ingest; fall back
                        // to the option snapshots (shape varies by lane).
                        const namedAxis = item.customization
                          ? orderedVariantLabel(item.variantSnapshot, item.customization)
                          : null;
                        const variantLabel =
                          namedAxis ??
                          item.variantTitle ??
                          buildVariantLabelFromSnapshots(
                            item.productOptions,
                            item.variantSnapshot,
                          );
                        return variantLabel ? (
                          <p className="truncate text-xs text-text-secondary">
                            {variantLabel}
                          </p>
                        ) : null;
                      })()}
                      {item.presetName && (
                        <p className="truncate text-xs text-text-secondary">{item.presetName}</p>
                      )}
                      {item.customization?.pricing?.formulaWidth != null &&
                        item.customization.pricing.formulaHeight != null && (
                        <p className="mt-1 text-xs text-text-secondary">
                          {item.customization.pricing.formulaWidth} × {item.customization.pricing.formulaHeight}{' '}
                          {item.customization.pricing.formulaUnitLabel ?? ''}
                          {item.customization.pricing.displayFormulaUnitCents != null
                            ? ` · ${(item.customization.pricing.displayFormulaUnitCents / 100).toFixed(2)} each`
                            : ''}
                          {item.customization.pricing.displayDiscountPercent
                            ? ` · ${item.customization.pricing.displayDiscountPercent}% off`
                            : ''}
                          {item.customization.pricing.displayLineTotalCents != null
                            ? ` · ${(item.customization.pricing.displayLineTotalCents / 100).toFixed(2)} line`
                            : ''}
                        </p>
                      )}
                      {item.customization && item.customization.selections.length > 0 && (
                        <div className="mt-1 flex flex-col gap-0.5">
                          {item.customization.selections.map((row) => (
                            <p key={`${row.label}:${row.value}`} className="text-xs text-text-secondary">
                              {row.label}: <span className="font-medium text-text-primary">{formatOrderedOptionValue(row.value)}</span>
                            </p>
                          ))}
                        </div>
                      )}
                      {item.customization && item.customization.uploads.length > 0 && (
                        <div className="mt-1.5 flex flex-col gap-1.5">
                          {item.customization.uploads.map((file, index) => (
                            <div key={file.token ?? `upload-${index}`} className="flex items-start gap-2">
                              {file.thumbUrl ? (
                                <img
                                  src={file.thumbUrl}
                                  alt=""
                                  className="h-16 w-16 rounded border border-border object-contain bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] [background-size:12px_12px]"
                                />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border text-[9px] text-text-tertiary">
                                  File
                                </div>
                              )}
                              <div className="min-w-0">
                                {file.label && (
                                  <p className="text-xs font-medium text-text-primary">{file.label}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {file.versionNo != null && (
                                    <Badge variant="info" size="sm">v{file.versionNo}</Badge>
                                  )}
                                  {file.scanStatus && (
                                    <Badge variant="default" size="sm">{file.scanStatus}</Badge>
                                  )}
                                  {file.previewUrl && (
                                    <a
                                      href={file.previewUrl}
                                      className="text-xs font-medium text-brand-600 hover:underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Preview
                                    </a>
                                  )}
                                  {file.downloadUrl && (
                                    <a
                                      href={file.downloadUrl}
                                      className="text-xs font-medium text-brand-600 hover:underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Download
                                    </a>
                                  )}
                                </div>
                                <p className="text-[10px] text-text-tertiary">
                                  {[
                                    file.mimeType,
                                    file.widthPx != null && file.heightPx != null
                                      ? `${file.widthPx}×${file.heightPx}`
                                      : null,
                                  ].filter(Boolean).join(' · ')}
                                </p>
                                {file.token && (
                                  <OptionUploadVersionActions
                                    token={file.token}
                                    fileLabel={file.label}
                                    basePath={`/api/v1/option-uploads/${file.token}`}
                                    orderQueryKey={['portal', 'orders']}
                                    linkClass="text-brand-600"
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-medium text-text-primary">
                        ${item.totalPrice}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-[11px] text-text-tertiary">
                          ${item.unitPrice} × {item.quantity}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Qty</span>
                      <span className="text-xs font-medium text-text-primary tabular-nums">{item.quantity}</span>
                    </div>
                    {item.sku && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">SKU</span>
                        <span className="font-mono text-xs text-text-primary bg-surface-secondary px-2 py-0.5 rounded border border-border">
                          {item.sku}
                        </span>
                      </div>
                    )}
                    {item.weightOz != null && item.weightOz > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Weight</span>
                        <span className="text-xs text-text-primary">{item.weightOz} oz</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Source</span>
                      {isUnrouted ? (
                        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium ${ROUTING_SOURCE_COLORS['UNROUTED']}`}>
                          Unrouted
                        </span>
                      ) : routingLabel ? (
                        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium ${ROUTING_SOURCE_COLORS[item.routingSource!] ?? 'bg-surface-secondary text-text-secondary border-border'}`}>
                          {routingLabel}
                        </span>
                      ) : (
                        <span className="text-xs text-text-tertiary">—</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Provider</span>
                      {isSelfFulfilled ? (
                        <span className="text-xs font-medium text-text-primary">
                          Self-fulfilled — you print and ship this item
                        </span>
                      ) : itemProvider ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-text-primary">
                          <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                          {itemProvider}
                        </span>
                      ) : (
                        <span className="text-xs text-text-tertiary italic">Pending</span>
                      )}
                    </div>
                    {/* Removed the legacy "Export → Download PNG"
                        (`item.exportOutputUrl`) block: it is the per-order
                        Export pipeline output, which is stale/shared across a
                        multi-sheet gangsheet and only ever appears after a
                        post-order edit re-runs the legacy lane. The live
                        per-sheet print files rendered by <DesignPartsSection>
                        below are the single source of truth. */}
                    {/* "View Preview" links to the design-level preview, which
                        is correct for apparel (the composite). For a gangsheet
                        every cart line shares the SAME design-level preview, so
                        showing it per line is misleading — each line's own sheet
                        is already shown in the thumbnail and the Print files
                        section below. Hide it for gangsheet lines (detected by a
                        SHEET design part; apparel never has one). */}
                    {!(item.designParts ?? []).some((p) => p.kind === 'SHEET') &&
                      (item.exportPreviewUrl || (item.sheetIndex == null && item.designPreviewUrl)) && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Preview</span>
                        <a
                          href={item.exportPreviewUrl || item.designPreviewUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-accent hover:underline"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Preview
                        </a>
                      </div>
                    )}
                    {item.actualWidthIn != null && item.actualHeightIn != null && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Print</span>
                        <span className="text-xs text-text-primary">{item.actualWidthIn}&times;{item.actualHeightIn}&quot;</span>
                      </div>
                    )}
                    {/* Customer-facing edit link (Shopify App Proxy) — the same
                        link the buyer received on their order line. Open +
                        copy so the merchant can re-share it with the customer. */}
                    {item.customerEditUrl && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Edit link</span>
                        <a
                          href={item.customerEditUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-w-0 items-center gap-1 text-xs font-medium text-text-secondary hover:text-accent hover:underline"
                          title={item.customerEditUrl}
                        >
                          <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                          </svg>
                          <span className="truncate">Customer edit link</span>
                        </a>
                        <button
                          type="button"
                          onClick={() => copyEditLink(item.id, item.customerEditUrl!)}
                          className="shrink-0 rounded-md border border-border bg-surface-secondary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary transition-colors hover:text-accent"
                        >
                          {copiedItemId === item.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    )}
                    <DesignPartsSection
                      parts={item.designParts}
                      previewImageUrl={item.variantPreviewUrl}
                      className="mt-1"
                    />
                    {/* Inline per-line "Edit design" — only for design-bound
                        STOREFRONT lines on surfaces that wired the launch
                        handler. Permission is resolved server-side; a blocked
                        edit explains why instead of opening the designer. */}
                    {(() => {
                      const eligible =
                        !!onEditDesign &&
                        item.designContext === 'STOREFRONT' &&
                        !!item.designId;
                      if (!eligible) return null;
                      const perm = item.editPermission;
                      const canEdit = perm?.canEdit ?? false;
                      const isOpening = editPendingItemId === item.id;
                      return (
                        <div className="pt-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (canEdit) {
                                onEditDesign(item.id);
                              } else if (perm?.message) {
                                onEditRestricted?.(perm.message);
                              }
                            }}
                            disabled={isOpening}
                            title={
                              !canEdit ? perm?.message ?? undefined : undefined
                            }
                            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                              canEdit
                                ? 'bg-brand-50 text-brand-primary hover:bg-brand-100'
                                : 'cursor-not-allowed bg-surface-secondary text-text-tertiary'
                            }`}
                          >
                            <svg
                              className="h-3 w-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"
                              />
                            </svg>
                            {isOpening ? 'Opening…' : 'Edit design'}
                          </button>
                          {!canEdit && perm?.message && (
                            <p className="mt-0.5 text-[10px] text-text-tertiary">
                              {perm.message}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isTerminal && (
        <div className="border-t border-border px-4 py-2.5 sm:px-5">
          <p className="text-[11px] text-text-tertiary">
            Final state — no further actions available.
          </p>
        </div>
      )}
    </Card>
  );
}

/**
 * Build a "White / S" variant label from the order-time snapshots when
 * `variantTitle` was not captured at ingest (older manual orders). Shapes
 * vary by lane:
 *   - Shopify `productOptions`: `{ Color: "White", Size: "S" }` record
 *   - manual `productOptions`: `[{ name, value }]` array
 *   - manual `variantSnapshot.options`: `[{ type, value }]` array
 */
function buildVariantLabelFromSnapshots(
  productOptions: unknown,
  variantSnapshot: unknown,
): string | null {
  const fromOptionList = (list: unknown): string | null => {
    if (!Array.isArray(list)) return null;
    const values = list
      .map((o) =>
        o && typeof o === 'object'
          ? (o as { value?: unknown }).value
          : undefined,
      )
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    return values.length > 0 ? values.join(' / ') : null;
  };

  if (Array.isArray(productOptions)) {
    const label = fromOptionList(productOptions);
    if (label) return label;
  } else if (productOptions && typeof productOptions === 'object') {
    const values = Object.values(productOptions as Record<string, unknown>)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (values.length > 0) return values.join(' / ');
  }

  if (variantSnapshot && typeof variantSnapshot === 'object') {
    return fromOptionList(
      (variantSnapshot as { options?: unknown }).options,
    );
  }
  return null;
}

